/**
 * UserDashboard - the home screen for someone who POSTS chores
 * ------------------------------------------------------------
 * Shows the headline numbers, who has applied and is waiting on an answer,
 * the jobs coming up, and every chore they have posted split into tabs.
 *
 * WHAT USED TO BE HERE. A "Needs your attention" panel held three different
 * things at once: applications waiting, reviews to write, and jobs with no
 * backup. The owner asked for it to go (18 September 2026), and each piece
 * landed somewhere it makes more sense:
 *
 *   applications waiting  a section of its own, below, with room to say who
 *                         is waiting and for how long
 *   reviews to write      the bell, and the screen straight after marking a
 *                         job done - see pages/ManageChore.jsx. Asking for a
 *                         review while the job is fresh gets a better review
 *                         than a list item found days later
 *   no backup student     the amber badge on the job itself under "Coming
 *                         up", where the job already is
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Users,
  AlertTriangle,
  ClipboardList,
  CreditCard,
} from 'lucide-react';

import { userApi, choreApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { money, dateTime, countdown } from '../utils/format';
import { choreWhenShort } from '../utils/schedule';
import ChoreCard from '../components/ChoreCard';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Badge from '../components/Badge';
import ConfirmEmailBanner from '../components/ConfirmEmailBanner';

import { useI18n } from '../hooks/useI18n';

/**
 * No "Cancelled" tab, at the owner's request (18 September 2026): a list of
 * jobs that are not happening serves nobody. A cancelled chore is still in
 * the database, and anybody who applied for it was told at the time - see
 * routes/chores.js.
 */
const TABS = [
  { key: 'open', label: 'tabs.open' },
  { key: 'assigned', label: 'tabs.bookedIn' },
  { key: 'completed', label: 'tabs.completed' },
  { key: 'expired', label: 'tabs.expired' },
];

export default function UserDashboard() {
  const { t } = useI18n();
  const { user, canPostChores, subscriptionsRequired } = useAuth();

  const [data, setData] = useState(null);
  const [chores, setChores] = useState([]);
  const [tab, setTab] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboard, mine] = await Promise.all([
        userApi.dashboard(),
        choreApi.mine(),
      ]);
      setData(dashboard);
      setChores(mine.chores);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader label={t('dashboard.loadingPoster')} />;

  if (error) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const visible = chores.filter((chore) => chore.status === tab);
  const countFor = (status) => chores.filter((c) => c.status === status).length;
  const choresAwaitingDecision = data?.choresAwaitingDecision || [];

  return (
    <div className="container section">
      <div className="row-between page-header">
        <div>
          <h1>{t('dashboard.hello', { name: user.firstName })}</h1>
          <p>{t('dashboard.posterSubtitle')}</p>
        </div>
        <Link to="/chores/new" className="btn btn-primary btn-inline">
          <Plus size={18} /> {t('dashboard.postChore')}
        </Link>
      </div>

      {/* Above the membership warning on purpose: an unconfirmed address
          blocks posting outright, so it is the more useful thing to read
          first. */}
      <ConfirmEmailBanner />

      {/* ---------------- MEMBERSHIP WARNING ---------------- */}
      {/* Only ever shown once the site actually charges. */}
      {subscriptionsRequired && !canPostChores && (
        <div className="alert alert-warning" style={{ marginBottom: 'var(--space-md)' }}>
          <CreditCard size={20} aria-hidden="true" />
          <div>
            <strong>{t('dashboard.membershipInactive')}</strong> {t('dashboard.membershipBody')}
            but you will need to join to post new chores.{' '}
            <Link to="/subscribe">{t('dashboard.setUpMembership')}</Link>
          </div>
        </div>
      )}

      {/* ---------------- NUMBERS ---------------- */}
      <div className="stat-grid" style={{ marginBottom: 'var(--space-lg)' }}>
        {/* "Chores posted" was a third tile here, a count of everything this
            poster had ever put up. Removed on request (18 September 2026) for
            the same reason as "Paid out to students" below it: a lifetime
            total is not a thing anybody needs on the way past. What is left
            is the two numbers somebody acts on. */}
        <div className="stat stat-amber">
          <div className="stat-value">{stats.pendingApplications || 0}</div>
          <div className="stat-label">{t('dashboard.applicationsWaiting')}</div>
        </div>
        <div className="stat stat-blue">
          <div className="stat-value">{stats.chores?.completed || 0}</div>
          <div className="stat-label">{t('dashboard.choresCompleted')}</div>
        </div>
        {/* There was a fourth tile here, "Paid out to students", showing a
            running total of everything this poster had ever paid. Removed on
            request, and it is no loss: the site does not handle the money, so
            the figure was only ever the sum of what the chores said they
            would pay - and a running total of what somebody has spent is not
            something they need shown to them every time they open the page. */}
      </div>

      {/* ---------------- WHO HAS APPLIED ---------------- */}
      {/* Its own section, at the owner's request: somebody is waiting on an
          answer, which is the one thing on this page where another person is
          held up by the poster not acting. Hidden entirely when nobody is
          waiting, rather than shown empty. */}
      {choresAwaitingDecision.length > 0 && (
        <section style={{ marginBottom: 'var(--space-lg)' }}>
          <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>
            {t('dashboard.whoApplied')}
          </h2>

          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {choresAwaitingDecision.map((chore) => (
              <div className="card" key={chore._id}>
                <div className="row-between">
                  {/* No icon beside the title. There was one, and on a phone a
                      long title wraps below it while a short one sits next to
                      it - so no two cards lined up. The heading above already
                      says what these are. */}
                  <div>
                    <Link to={`/dashboard/chores/${chore._id}`} className="card-title">
                      {chore.title}
                    </Link>
                    <span className="card-meta" style={{ display: 'block', marginTop: 4 }}>
                      {t('dashboard.waitingOnYou', { count: chore.waiting })}
                    </span>
                  </div>
                  {/* Applications are decided on the chore's own page, which
                      is where this goes. It used to switch to the "Open" tab
                      - the tab you were already on, so nothing happened. */}
                  <Link
                    to={`/dashboard/chores/${chore._id}`}
                    className="btn btn-outline btn-sm btn-inline"
                  >
                    {t('dashboard.seeWhoApplied')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- UPCOMING ---------------- */}
      {data?.upcomingJobs?.length > 0 && (
        <section style={{ marginBottom: 'var(--space-lg)' }}>
          <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>{t('dashboard.comingUp')}</h2>

          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {data.upcomingJobs.map((job) => (
              <div className="card" key={job._id}>
                <div className="row-between">
                  <div>
                    <Link to={`/dashboard/chores/${job._id}`} className="card-title">
                      {job.title}
                    </Link>
                    <div className="card-meta" style={{ marginTop: 4 }}>
                      {/* The next visit, not the whole job - see the same
                          note on StudentDashboard.jsx. */}
                      <span>
                        {job.isMultiDay
                          ? `next ${choreWhenShort(job)} · ${job.visitCount} visits`
                          : choreWhenShort(job)}
                      </span>
                      <span>{countdown(job.hoursUntilStart)}</span>
                      <span>{money(job.isMultiDay ? job.totalPay : job.pay)}</span>
                    </div>
                  </div>

                  <div className="row" style={{ gap: 'var(--space-xs)' }}>
                    {job.assignedStudent && (
                      <Badge tone="green">
                        {job.assignedStudent.firstName} {job.assignedStudent.lastName?.charAt(0)}.
                      </Badge>
                    )}
                    {job.backupStudent ? (
                      <Badge tone="soft-blue">
                        Backup: {job.backupStudent.firstName}
                      </Badge>
                    ) : (
                      <Badge tone="amber" icon={AlertTriangle}>
                        {t('dashboard.noBackup')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- ALL CHORES ---------------- */}
      <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>{t('dashboard.yourChores')}</h2>

      <div className="tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`tab${tab === item.key ? ' active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {t(item.label)}
            <span className="tab-count">{countFor(item.key)}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={t('tabs.noneOfThese', {
            kind: t(TABS.find((item) => item.key === tab)?.label || 'tabs.open').toLowerCase(),
          })}
          message={
            tab === 'open'
              ? 'Post a chore and students nearby will be able to apply for it.'
              : 'Nothing here yet.'
          }
          action={
            tab === 'open' ? (
              <Link to="/chores/new" className="btn btn-primary btn-inline">
                <Plus size={18} /> {t('dashboard.postAChore')}
              </Link>
            ) : null
          }
        />
      ) : (
        <div className="grid-cards">
          {visible.map((chore) => (
            <div key={chore._id} className="stack" style={{ gap: 'var(--space-xs)' }}>
              <ChoreCard chore={chore} showStatus showApplicants />
              <Link to={`/dashboard/chores/${chore._id}`} className="btn btn-outline btn-sm btn-block">
                <Users size={16} /> Manage
                {chore.applicationCount > 0 ? ` (${chore.applicationCount})` : ''}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
